import type {
  PresentationAuditEntry,
  PresentationAuditLinkKind,
} from '../services/programPresentationAuditService';
import type { ProgramProgressCell } from '../types/domain';
import type { ProgramContentSlideOwnerRef } from '../types/programContentSlideCanvas';
import type { PresentationRefSlideLinkState } from './programPresentationLinkState';
import { formatLearningTextForDisplay, parseMemoFieldStorageDeep } from './programCellDisplayText';
import { pickProgramContentSlideOwnerWithDbPresence } from './programContentSlideOwner';
import { programContentSlidePresenceKey } from '../services/programContentSlideService';
import type { TrackProgramMode } from './trackProgramModeStorage';

export type ProgramPresentationListItem = {
  annualCellKey: string;
  dateLabel: string;
  weekdayLabel: string;
  periodLabel: string;
  templateLabel: string;
  timetableText: string;
  unitText: string;
  learningText: string;
  /** 정렬용: mondayIso|weekday|slotIndex|order */
  sortKey: string;
  slideOwner: ProgramContentSlideOwnerRef | null;
  isDraftOnly: boolean;
  /** 동기화 편제에서 대표(원본)가 아닌 미러 행 — 목록에서 흐리게·비활성 */
  isSyncMirror?: boolean;
  /** 편제 원본·learning_text ref와 연결되지 않은 슬라이드 */
  isOrphan?: boolean;
  orphanLinkLabel?: string;
  pageCount?: number;
  layoutBytes?: number;
  hasPresentationRef?: boolean;
  slideOwnerCandidates?: ProgramContentSlideOwnerRef[];
  linkState?: PresentationRefSlideLinkState | null;
  linkStateLabel?: string;
};

export type PresentationListOrphanLookup = {
  cellById?: Map<string, ProgramProgressCell>;
  periodLabelForSlot?: (slotIndex: number) => string;
  weekdayLabelForKey?: (weekdayKey: string) => string;
  shortDateLabelForIso?: (iso: string) => string;
};

export function isPresentationAuditOrphan(linkKind: PresentationAuditLinkKind): boolean {
  return linkKind === 'orphan_unreferenced_ref' || linkKind === 'orphan_owner_missing';
}

/** 현재 화면 스코프(편제·프로젝트·고정 행)에 속한 owner만 고아로 표시 */
export function filterPresentationOrphansToPageScope(
  entries: PresentationAuditEntry[],
  params: {
    scopedRowIds: ReadonlySet<string>;
    fixedRowIds: ReadonlySet<string>;
    programCellIds: ReadonlySet<string>;
  }
): PresentationAuditEntry[] {
  return entries.filter((entry) => {
    if (!isPresentationAuditOrphan(entry.linkKind)) return false;
    const id = entry.ownerId.trim();
    if (!id) return false;
    if (entry.ownerKind === 'scoped_row') return params.scopedRowIds.has(id);
    if (entry.ownerKind === 'fixed_row') return params.fixedRowIds.has(id);
    if (entry.ownerKind === 'program_cell') return params.programCellIds.has(id);
    return false;
  });
}

export function presentationListItemOwnerKey(item: ProgramPresentationListItem): string | null {
  if (!item.slideOwner?.id?.trim()) return null;
  return programContentSlidePresenceKey(item.slideOwner);
}

export function auditEntryToPresentationListItem(
  entry: PresentationAuditEntry,
  lookup?: PresentationListOrphanLookup
): ProgramPresentationListItem {
  const owner: ProgramContentSlideOwnerRef = { kind: entry.ownerKind, id: entry.ownerId };
  const cell = entry.ownerKind === 'program_cell' ? lookup?.cellById?.get(entry.ownerId) : undefined;

  let dateLabel = '—';
  let weekdayLabel = '—';
  let periodLabel = '—';
  let templateLabel = '고아';
  let timetableText = entry.contextLabel;
  let unitText = '—';
  let learningText =
    entry.learningSnippet && entry.learningSnippet !== '—' ? entry.learningSnippet : entry.linkLabel;
  let sortKey = `zz|orphan|${entry.selectionKey}`;

  if (cell) {
    dateLabel = lookup?.shortDateLabelForIso?.(cell.monday_iso) ?? cell.monday_iso;
    weekdayLabel = lookup?.weekdayLabelForKey?.(cell.weekday_key) ?? cell.weekday_key;
    periodLabel =
      lookup?.periodLabelForSlot?.(cell.slot_index) ?? `${Number(cell.slot_index) + 1}타임`;
    timetableText = (cell.timetable_text ?? '').trim() || entry.contextLabel;
    const unitRaw = formatLearningTextForDisplay(parseMemoFieldStorageDeep(cell.unit_text ?? '').text).trim();
    unitText = unitRaw || '—';
    const learningRaw = formatLearningTextForDisplay(
      parseMemoFieldStorageDeep(cell.learning_text ?? '').text
    ).trim();
    if (learningRaw) learningText = learningRaw;
    sortKey = `${cell.monday_iso}|${cell.weekday_key}|${String(cell.slot_index).padStart(2, '0')}|orphan|${entry.ownerId.slice(0, 8)}`;
  } else if (entry.ownerKind === 'presentation_ref') {
    templateLabel = '고아 ref';
  }

  return {
    annualCellKey: entry.selectionKey,
    dateLabel,
    weekdayLabel,
    periodLabel,
    templateLabel,
    timetableText,
    unitText,
    learningText,
    sortKey,
    slideOwner: owner,
    isDraftOnly: false,
    isOrphan: true,
    orphanLinkLabel: entry.linkLabel,
    pageCount: entry.pageCount,
  };
}

export function mergeOrphanPresentationListItems(
  linkedItems: ProgramPresentationListItem[],
  orphanEntries: PresentationAuditEntry[],
  lookup?: PresentationListOrphanLookup
): ProgramPresentationListItem[] {
  const coveredOwners = new Set<string>();
  linkedItems.forEach((item) => {
    const key = presentationListItemOwnerKey(item);
    if (key) coveredOwners.add(key);
  });

  const orphans: ProgramPresentationListItem[] = [];
  orphanEntries.forEach((entry) => {
    if (!isPresentationAuditOrphan(entry.linkKind)) return;
    const ownerKey = programContentSlidePresenceKey({ kind: entry.ownerKind, id: entry.ownerId });
    if (coveredOwners.has(ownerKey)) return;
    coveredOwners.add(ownerKey);
    orphans.push(auditEntryToPresentationListItem(entry, lookup));
  });

  return sortPresentationListItems([...linkedItems, ...orphans]);
}

/** 편제 동기화 미러: 대표 시간표명과 다른 alias 행은 같은 프레젠테이션을 공유한다 */
export function resolveAnnualPresentationSyncMirror(params: {
  programKindLabel: string;
  isFixedSlot: boolean;
  templateLabel: string;
  scopeLookupAlias: string;
  trackProgramModeByTrack: Record<string, TrackProgramMode>;
  trackProgramSyncRepByTrack: Record<string, string>;
}): boolean {
  const template = params.templateLabel.trim();
  if (!template || params.isFixedSlot || params.programKindLabel === '프로') return false;
  const mode = params.trackProgramModeByTrack[template] ?? 'unified';
  if (mode !== 'sync') return false;
  const syncRep = (params.trackProgramSyncRepByTrack[template] ?? '').trim();
  const alias = params.scopeLookupAlias.trim();
  if (!syncRep || !alias) return false;
  return alias !== syncRep;
}

export function formatPresentationLayoutBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(bytes < 10_240 ? 1 : 0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function resolvePresentationListSlideOwner(params: {
  candidates: ProgramContentSlideOwnerRef[];
  presenceKeys: ReadonlySet<string>;
  fallbackOwner: ProgramContentSlideOwnerRef | null;
}): ProgramContentSlideOwnerRef | null {
  return pickProgramContentSlideOwnerWithDbPresence(
    params.candidates,
    params.presenceKeys,
    programContentSlidePresenceKey,
    params.fallbackOwner
  );
}

export function sortPresentationListItems(items: ProgramPresentationListItem[]): ProgramPresentationListItem[] {
  return [...items].sort((a, b) => a.sortKey.localeCompare(b.sortKey, 'en'));
}
